import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

const warehouses = [
  { region: "USA", flag: "🇺🇸", city: "Los Angeles, CA", area: "120,000 sqft", skus: "28,000+", delivery: "1–3 days" },
  { region: "USA", flag: "🇺🇸", city: "New York, NY", area: "95,000 sqft", skus: "22,000+", delivery: "1–3 days" },
  { region: "USA", flag: "🇺🇸", city: "Chicago, IL", area: "80,000 sqft", skus: "18,000+", delivery: "1–3 days" },
  { region: "USA", flag: "🇺🇸", city: "Houston, TX", area: "75,000 sqft", skus: "15,000+", delivery: "1–3 days" },
  { region: "USA", flag: "🇺🇸", city: "Seattle, WA", area: "60,000 sqft", skus: "12,000+", delivery: "1–3 days" },
  { region: "Canada", flag: "🇨🇦", city: "Toronto, ON", area: "70,000 sqft", skus: "14,000+", delivery: "2–4 days" },
  { region: "Canada", flag: "🇨🇦", city: "Vancouver, BC", area: "55,000 sqft", skus: "10,000+", delivery: "2–4 days" },
  { region: "Canada", flag: "🇨🇦", city: "Montreal, QC", area: "48,000 sqft", skus: "9,500+", delivery: "2–4 days" },
  { region: "Europe", flag: "🇪🇺", city: "Rotterdam, Netherlands", area: "150,000 sqft", skus: "35,000+", delivery: "2–5 days" },
  { region: "Europe", flag: "🇪🇺", city: "Hamburg, Germany", area: "120,000 sqft", skus: "28,000+", delivery: "2–5 days" },
  { region: "Europe", flag: "🇪🇺", city: "Paris, France", area: "90,000 sqft", skus: "20,000+", delivery: "2–5 days" },
  { region: "Europe", flag: "🇪🇺", city: "Milan, Italy", area: "75,000 sqft", skus: "16,000+", delivery: "2–5 days" },
  { region: "Europe", flag: "🇪🇺", city: "Madrid, Spain", area: "65,000 sqft", skus: "13,000+", delivery: "2–5 days" },
  { region: "Europe", flag: "🇪🇺", city: "Warsaw, Poland", area: "55,000 sqft", skus: "11,000+", delivery: "2–5 days" },
  { region: "Europe", flag: "🇪🇺", city: "Amsterdam, Netherlands", area: "50,000 sqft", skus: "10,000+", delivery: "2–5 days" },
  { region: "Africa", flag: "🌍", city: "Lagos, Nigeria", area: "45,000 sqft", skus: "8,000+", delivery: "4–7 days" },
  { region: "Africa", flag: "🌍", city: "Nairobi, Kenya", area: "38,000 sqft", skus: "7,000+", delivery: "4–7 days" },
  { region: "Africa", flag: "🌍", city: "Cairo, Egypt", area: "42,000 sqft", skus: "8,500+", delivery: "4–7 days" },
  { region: "Africa", flag: "🌍", city: "Johannesburg, SA", area: "50,000 sqft", skus: "9,000+", delivery: "4–7 days" },
  { region: "Africa", flag: "🌍", city: "Casablanca, Morocco", area: "35,000 sqft", skus: "6,500+", delivery: "4–7 days" },
  { region: "Latin America", flag: "🌎", city: "São Paulo, Brazil", area: "80,000 sqft", skus: "17,000+", delivery: "3–6 days" },
  { region: "Latin America", flag: "🌎", city: "Mexico City, Mexico", area: "65,000 sqft", skus: "13,000+", delivery: "3–6 days" },
  { region: "Latin America", flag: "🌎", city: "Buenos Aires, Argentina", area: "50,000 sqft", skus: "10,000+", delivery: "3–6 days" },
  { region: "Latin America", flag: "🌎", city: "Bogotá, Colombia", area: "42,000 sqft", skus: "8,000+", delivery: "3–6 days" },
  { region: "South Asia", flag: "🌏", city: "Mumbai, India", area: "70,000 sqft", skus: "15,000+", delivery: "3–5 days" },
  { region: "South Asia", flag: "🌏", city: "Delhi, India", area: "60,000 sqft", skus: "12,000+", delivery: "3–5 days" },
  { region: "South Asia", flag: "🌏", city: "Dhaka, Bangladesh", area: "40,000 sqft", skus: "7,500+", delivery: "3–5 days" },
  { region: "South Asia", flag: "🌏", city: "Colombo, Sri Lanka", area: "35,000 sqft", skus: "6,000+", delivery: "3–5 days" },
];

const regionColors: Record<string, { bg: string; badge: string; text: string }> = {
  USA: { bg: "bg-red-50 border-red-100", badge: "bg-red-100 text-red-600", text: "text-red-600" },
  Canada: { bg: "bg-orange-50 border-orange-100", badge: "bg-orange-100 text-orange-600", text: "text-orange-600" },
  Europe: { bg: "bg-amber-50 border-amber-100", badge: "bg-amber-100 text-amber-600", text: "text-amber-600" },
  Africa: { bg: "bg-green-50 border-green-100", badge: "bg-green-100 text-green-700", text: "text-green-700" },
  "Latin America": { bg: "bg-teal-50 border-teal-100", badge: "bg-teal-100 text-teal-700", text: "text-teal-700" },
  "South Asia": { bg: "bg-rose-50 border-rose-100", badge: "bg-rose-100 text-rose-600", text: "text-rose-600" },
};

const regions = ["All", "USA", "Canada", "Europe", "Africa", "Latin America", "South Asia"];

const seaRoutes = [
  { from: "Shanghai", to: "Los Angeles", transit: "14–18 days", freq: "Daily", type: "FCL / LCL" },
  { from: "Shenzhen", to: "New York", transit: "22–26 days", freq: "3x/week", type: "FCL / LCL" },
  { from: "Guangzhou", to: "Rotterdam", transit: "25–30 days", freq: "Daily", type: "FCL / LCL" },
  { from: "Tianjin", to: "Hamburg", transit: "28–32 days", freq: "2x/week", type: "FCL / LCL" },
  { from: "Qingdao", to: "Vancouver", transit: "16–20 days", freq: "3x/week", type: "FCL / LCL" },
  { from: "Ningbo", to: "São Paulo", transit: "35–40 days", freq: "Weekly", type: "FCL / LCL" },
];

const airRoutes = [
  { from: "Shanghai PVG", to: "Los Angeles LAX", transit: "12–16 hrs", freq: "Daily", type: "Express / Standard" },
  { from: "Beijing PEK", to: "New York JFK", transit: "14–18 hrs", freq: "Daily", type: "Express / Standard" },
  { from: "Guangzhou CAN", to: "London LHR", transit: "13–17 hrs", freq: "Daily", type: "Express / Standard" },
  { from: "Shenzhen SZX", to: "Frankfurt FRA", transit: "12–16 hrs", freq: "Daily", type: "Express / Standard" },
  { from: "Chengdu CTU", to: "Toronto YYZ", transit: "15–19 hrs", freq: "4x/week", type: "Express / Standard" },
  { from: "Hong Kong HKG", to: "Dubai DXB", transit: "8–10 hrs", freq: "Daily", type: "Express / Standard" },
];

const expressZones = [
  { zone: "North America", countries: "USA, Canada, Mexico", transit: "3–5 days", carrier: "FedEx / UPS / DHL" },
  { zone: "Europe", countries: "EU 27 + UK, Switzerland", transit: "4–6 days", carrier: "DHL / TNT / DPD" },
  { zone: "South Asia", countries: "India, Bangladesh, Sri Lanka", transit: "5–7 days", carrier: "DHL / Aramex" },
  { zone: "Latin America", countries: "Brazil, Mexico, Colombia, Argentina", transit: "6–9 days", carrier: "DHL / FedEx" },
  { zone: "Africa", countries: "Nigeria, Kenya, Egypt, South Africa", transit: "7–10 days", carrier: "Aramex / DHL" },
  { zone: "Middle East", countries: "UAE, Saudi Arabia, Qatar", transit: "4–6 days", carrier: "Aramex / DHL" },
];

const truckingServices = [
  {
    icon: "ri-truck-line",
    title: "FTL (Full Truckload)",
    badge: "Most Popular",
    badgeColor: "bg-amber-100 text-amber-700",
    desc: "Dedicated truck exclusively for your cargo. Best for large shipments over 10,000 lbs. Door-to-door across USA, Canada & Mexico with GPS tracking.",
    price: "From $1.20/mile",
    features: ["Dedicated 53ft dry van or reefer", "Direct delivery, no stops", "GPS real-time tracking", "Driver contact included"],
  },
  {
    icon: "ri-box-3-line",
    title: "LTL (Less than Truckload)",
    badge: "Cost Effective",
    badgeColor: "bg-green-100 text-green-700",
    desc: "Share truck space with other shippers. Ideal for smaller loads under 10,000 lbs. Consolidated pickups from our 28 warehouses.",
    price: "From $0.85/mile",
    features: ["Freight class pricing", "Pickup within 24 hrs", "Consolidated from warehouses", "Liftgate available"],
  },
  {
    icon: "ri-building-4-line",
    title: "Dedicated Fleet Contracts",
    badge: "Enterprise",
    badgeColor: "bg-stone-100 text-stone-700",
    desc: "Assign dedicated trucks and drivers exclusively to your business. Guaranteed capacity, consistent service, and volume pricing.",
    price: "Custom pricing",
    features: ["Branded truck wraps available", "Assigned driver team", "Monthly volume contracts", "Priority dispatch 24/7"],
  },
  {
    icon: "ri-flag-line",
    title: "Cross-Border Trucking",
    badge: "VERY IMPORTANT",
    badgeColor: "bg-red-100 text-red-600",
    desc: "Seamless cross-border trucking between USA, Canada & Mexico. USMCA-compliant with in-house customs clearance and border transfer coordination.",
    price: "From $1.80/mile",
    features: ["USA ↔ Canada ↔ Mexico", "USMCA compliance handled", "In-house customs clearance", "Border transfer coordination"],
  },
];

const crossBorderRoutes = [
  { from: "🇺🇸 Los Angeles, CA", to: "🇲🇽 Tijuana / Mexico City", transit: "1–3 days", border: "San Ysidro / Otay Mesa", freq: "Daily" },
  { from: "🇺🇸 Laredo, TX", to: "🇲🇽 Monterrey / Guadalajara", transit: "1–2 days", border: "Laredo–Nuevo Laredo", freq: "Daily" },
  { from: "🇺🇸 Detroit, MI", to: "🇨🇦 Toronto / Windsor", transit: "Same day", border: "Ambassador Bridge", freq: "Daily" },
  { from: "🇺🇸 Seattle, WA", to: "🇨🇦 Vancouver / Surrey", transit: "Same day", border: "Peace Arch / Pacific Hwy", freq: "Daily" },
  { from: "🇨🇦 Toronto, ON", to: "🇺🇸 Buffalo / New York", transit: "Same day", border: "Peace Bridge", freq: "Daily" },
  { from: "🇨🇦 Vancouver, BC", to: "🇺🇸 Seattle / Portland", transit: "Same day", border: "Pacific Highway", freq: "Daily" },
  { from: "🇲🇽 Mexico City", to: "🇺🇸 El Paso / Dallas", transit: "2–3 days", border: "El Paso–Ciudad Juárez", freq: "3x/week" },
  { from: "🇺🇸 Chicago, IL", to: "🇨🇦 Montreal / Ottawa", transit: "1–2 days", border: "Champlain / Lacolle", freq: "Daily" },
];

export default function FreightServicesPage() {
  useSEO({
    title: "Global Freight Services | Sea, Air, Express & Trucking | Crown Global",
    description: "Crown Global offers sea freight (FCL/LCL), air freight, express delivery, cross-border trucking (USA/Canada/Mexico), customs clearance, and 28 overseas warehouses worldwide. Get a free quote today.",
    keywords: "sea freight, air freight, express delivery, cross-border trucking, customs clearance, overseas warehouse, global logistics",
    canonical: "/freight-services",
    ogTitle: "Global Freight Services — Crown Global",
    ogDescription: "Sea freight, air freight, express delivery, cross-border trucking, customs clearance, and 28 overseas warehouses. Get a free quote.",
    ogImage: "https://public.readdy.ai/ai/img_res/e48967b60b1e0ff4eb1c41724ac0cc64.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/freight-services`,
          "url": `${SITE_URL}/freight-services`,
          "name": "Global Freight Services — Crown Global",
          "description": "Comprehensive global freight services including sea freight, air freight, express delivery, cross-border trucking, customs clearance, and 28 overseas warehouses.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Freight Services", "item": `${SITE_URL}/freight-services` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/freight-services#service`,
          "url": `${SITE_URL}/freight-services`,
          "name": "Crown Global Freight & Logistics Services",
          "description": "Comprehensive global freight services including sea freight (FCL/LCL), air freight, express delivery, cross-border trucking across USA, Canada and Mexico, customs clearance, and 28 overseas warehouses.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": "Worldwide",
          "serviceType": ["Sea Freight", "Air Freight", "Express Delivery", "Cross-Border Trucking", "Customs Clearance", "Overseas Warehousing"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Freight Service Packages",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "FCL Sea Freight", "description": "Full Container Load sea freight from China to worldwide ports" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "LCL Sea Freight", "description": "Less than Container Load consolidated sea freight" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Air Freight", "description": "Daily air freight flights from China to North America and Europe" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Cross-Border Trucking", "description": "60-truck fleet for USA, Canada and Mexico cross-border trucking" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Customs Clearance", "description": "Professional customs declaration and clearance in China and North America" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Overseas Warehousing", "description": "28 overseas warehouses across USA, Canada, Europe, Africa, Latin America and South Asia" } }
            ]
          }
        }
      ]
    }
  });

  const [activeTab, setActiveTab] = useState<"sea" | "air" | "express" | "trucking" | "customs" | "warehouses">("sea");
  const [regionFilter, setRegionFilter] = useState("All");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: "", email: "", service: "Sea Freight", origin: "", destination: "", weight: "", notes: "" });
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  const filteredWarehouses = regionFilter === "All" ? warehouses : warehouses.filter((w) => w.region === regionFilter);

  const handleQuoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setQuoteSubmitted(true);
    setTimeout(() => { setQuoteOpen(false); setQuoteSubmitted(false); }, 2500);
  };

  const tabs = [
    { id: "sea", label: "Sea Freight", icon: "ri-ship-line" },
    { id: "air", label: "Air Freight", icon: "ri-plane-line" },
    { id: "express", label: "Express Delivery", icon: "ri-flashlight-line" },
    { id: "trucking", label: "Cross-Border Trucking", icon: "ri-truck-line" },
    { id: "customs", label: "Customs Clearance", icon: "ri-government-line" },
    { id: "warehouses", label: "28 Warehouses", icon: "ri-building-2-line" },
  ] as const;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=global%20logistics%20freight%20shipping%20containers%20cargo%20plane%20truck%20aerial%20view%20professional%20commercial%20photography%20dramatic%20lighting%20world%20map%20overlay%20modern%20industrial&width=1600&height=700&seq=freight_hero_01&orientation=landscape"
            alt="Global Freight Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-900/60 to-stone-900/30"></div>
        </div>
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <span className="inline-block bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">Global Logistics</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4">
              Sea · Air · Express<br />Trucking &amp; Customs
            </h1>
            <p className="text-stone-200 text-base md:text-lg leading-relaxed mb-8">
              28 overseas warehouses across USA, Canada, Europe, Africa, Latin America &amp; South Asia. FCL/LCL sea freight, daily air flights, door-to-door express, and professional customs declaration &amp; clearance in China and North America.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setQuoteOpen(true)}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-7 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i> Get a Free Quote
              </button>
              <button
                onClick={() => setActiveTab("warehouses")}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-7 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-building-2-line"></i> View 28 Warehouses
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-amber-400">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-5 grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { icon: "ri-building-2-line", value: "28", label: "Overseas Warehouses" },
            { icon: "ri-truck-line", value: "60", label: "Owned Trucks" },
            { icon: "ri-ship-line", value: "120+", label: "Sea Routes" },
            { icon: "ri-plane-line", value: "Daily", label: "Air Flights" },
            { icon: "ri-government-line", value: "99.2%", label: "Customs Clearance Rate" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                <i className={`${s.icon} text-white text-lg`}></i>
              </div>
              <div>
                <div className="text-white font-extrabold text-xl leading-none">{s.value}</div>
                <div className="text-amber-100 text-xs mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tab navigation */}
      <section className="sticky top-[104px] z-30 bg-white border-b border-stone-100 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-amber-400 text-white"
                    : "text-stone-600 hover:bg-stone-100"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-14">

        {/* SEA FREIGHT */}
        {activeTab === "sea" && (
          <div>
            <div className="flex flex-col md:flex-row gap-10 mb-14">
              <div className="flex-1">
                <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Ocean Shipping</span>
                <h2 className="text-3xl font-extrabold text-stone-900 mt-1 mb-4">Sea Freight Services</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  We provide cost-effective <strong>Full Container Load (FCL)</strong> and <strong>Less than Container Load (LCL)</strong> solutions, with regular sailings between China and major ports worldwide. Our 28 overseas warehouses ensure fast last-mile delivery once cargo arrives.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: "ri-ship-line", title: "FCL Solutions", desc: "20ft & 40ft containers, dedicated space, best rate for large volumes" },
                    { icon: "ri-box-3-line", title: "LCL Solutions", desc: "Share container space, ideal for smaller shipments, weekly consolidation" },
                    { icon: "ri-anchor-line", title: "Port Coverage", desc: "All major Chinese ports to 80+ destination ports worldwide" },
                    { icon: "ri-shield-check-line", title: "Cargo Insurance", desc: "Full cargo insurance available, covering all-risk and total loss" },
                  ].map((f) => (
                    <div key={f.title} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                      <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg mb-3">
                        <i className={`${f.icon} text-amber-500 text-base`}></i>
                      </div>
                      <h4 className="font-bold text-stone-800 text-sm mb-1">{f.title}</h4>
                      <p className="text-stone-500 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
                <button onClick={() => setQuoteOpen(true)} className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i> Request Sea Freight Quote
                </button>
              </div>
              <div className="w-full md:w-96 shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=large%20cargo%20container%20ship%20ocean%20freight%20shipping%20port%20terminal%20aerial%20view%20professional%20commercial%20photography%20dramatic%20sunset%20lighting%20containers%20stacked&width=800&height=560&seq=sea_freight_img_01&orientation=landscape"
                  alt="Sea Freight"
                  className="w-full h-72 object-cover object-top rounded-2xl"
                />
              </div>
            </div>

            {/* Routes table */}
            <h3 className="text-xl font-extrabold text-stone-900 mb-5">Popular Sea Routes</h3>
            <div className="overflow-x-auto rounded-2xl border border-stone-100">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    {["Origin Port", "Destination Port", "Transit Time", "Frequency", "Service Type"].map((h) => (
                      <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {seaRoutes.map((r, i) => (
                    <tr key={i} className="border-t border-stone-100 hover:bg-amber-50/40 transition-colors">
                      <td className="px-5 py-4 font-semibold text-stone-800">{r.from}</td>
                      <td className="px-5 py-4 text-stone-600">{r.to}</td>
                      <td className="px-5 py-4"><span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">{r.transit}</span></td>
                      <td className="px-5 py-4 text-stone-600">{r.freq}</td>
                      <td className="px-5 py-4"><span className="bg-stone-100 text-stone-600 text-xs font-medium px-2.5 py-1 rounded-full">{r.type}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AIR FREIGHT */}
        {activeTab === "air" && (
          <div>
            <div className="flex flex-col md:flex-row gap-10 mb-14">
              <div className="flex-1">
                <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Aviation Logistics</span>
                <h2 className="text-3xl font-extrabold text-stone-900 mt-1 mb-4">Air Freight Services</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  Fast and reliable air freight services, featuring <strong>daily flights</strong> connecting China and North America to meet your time-sensitive requirements. We partner with major airlines to guarantee capacity and competitive rates year-round.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: "ri-plane-line", title: "Daily Flights", desc: "Daily departures from all major Chinese airports to North America, Europe & beyond" },
                    { icon: "ri-time-line", title: "Express Air", desc: "12–18 hour transit times for urgent shipments, priority handling guaranteed" },
                    { icon: "ri-temp-cold-line", title: "Cold Chain", desc: "Temperature-controlled air freight for pharmaceuticals, food & perishables" },
                    { icon: "ri-file-text-line", title: "Customs Clearance", desc: "Full customs brokerage service at origin and destination included" },
                  ].map((f) => (
                    <div key={f.title} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                      <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg mb-3">
                        <i className={`${f.icon} text-amber-500 text-base`}></i>
                      </div>
                      <h4 className="font-bold text-stone-800 text-sm mb-1">{f.title}</h4>
                      <p className="text-stone-500 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
                <button onClick={() => setQuoteOpen(true)} className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i> Request Air Freight Quote
                </button>
              </div>
              <div className="w-full md:w-96 shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=cargo%20airplane%20freight%20aircraft%20loading%20airport%20tarmac%20professional%20commercial%20photography%20dramatic%20sky%20lighting%20cargo%20containers%20being%20loaded%20wide%20body%20aircraft&width=800&height=560&seq=air_freight_img_02&orientation=landscape"
                  alt="Air Freight"
                  className="w-full h-72 object-cover object-top rounded-2xl"
                />
              </div>
            </div>

            <h3 className="text-xl font-extrabold text-stone-900 mb-5">Popular Air Routes</h3>
            <div className="overflow-x-auto rounded-2xl border border-stone-100">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    {["Origin Airport", "Destination Airport", "Transit Time", "Frequency", "Service Type"].map((h) => (
                      <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {airRoutes.map((r, i) => (
                    <tr key={i} className="border-t border-stone-100 hover:bg-amber-50/40 transition-colors">
                      <td className="px-5 py-4 font-semibold text-stone-800">{r.from}</td>
                      <td className="px-5 py-4 text-stone-600">{r.to}</td>
                      <td className="px-5 py-4"><span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">{r.transit}</span></td>
                      <td className="px-5 py-4 text-stone-600">{r.freq}</td>
                      <td className="px-5 py-4"><span className="bg-stone-100 text-stone-600 text-xs font-medium px-2.5 py-1 rounded-full">{r.type}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* EXPRESS DELIVERY */}
        {activeTab === "express" && (
          <div>
            <div className="flex flex-col md:flex-row gap-10 mb-14">
              <div className="flex-1">
                <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Door-to-Door</span>
                <h2 className="text-3xl font-extrabold text-stone-900 mt-1 mb-4">Express Delivery Services</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  Global door-to-door express delivery services, featuring <strong>real-time tracking</strong> and optimized delivery routes. We partner with FedEx, UPS, DHL, and Aramex to ensure your packages arrive on time, every time.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: "ri-map-pin-line", title: "Real-Time Tracking", desc: "Track your shipment 24/7 with live GPS updates and delivery notifications" },
                    { icon: "ri-route-line", title: "Optimized Routes", desc: "AI-powered route optimization ensures fastest possible delivery times" },
                    { icon: "ri-home-line", title: "Door-to-Door", desc: "Full pickup and delivery service from sender's door to recipient's door" },
                    { icon: "ri-customer-service-2-line", title: "24/7 Support", desc: "Round-the-clock customer support for all express shipment inquiries" },
                  ].map((f) => (
                    <div key={f.title} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                      <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg mb-3">
                        <i className={`${f.icon} text-amber-500 text-base`}></i>
                      </div>
                      <h4 className="font-bold text-stone-800 text-sm mb-1">{f.title}</h4>
                      <p className="text-stone-500 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
                <button onClick={() => setQuoteOpen(true)} className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i> Request Express Quote
                </button>
              </div>
              <div className="w-full md:w-96 shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=express%20delivery%20courier%20van%20packages%20door%20to%20door%20delivery%20service%20professional%20commercial%20photography%20urban%20street%20modern%20logistics%20last%20mile%20delivery&width=800&height=560&seq=express_img_03&orientation=landscape"
                  alt="Express Delivery"
                  className="w-full h-72 object-cover object-top rounded-2xl"
                />
              </div>
            </div>

            <h3 className="text-xl font-extrabold text-stone-900 mb-5">Express Delivery Zones</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {expressZones.map((z) => (
                <div key={z.zone} className="bg-stone-50 border border-stone-100 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-extrabold text-stone-800 text-base">{z.zone}</h4>
                    <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">{z.transit}</span>
                  </div>
                  <p className="text-stone-500 text-xs mb-3">{z.countries}</p>
                  <div className="flex items-center gap-1.5 text-xs text-stone-600">
                    <i className="ri-truck-line text-amber-500"></i>
                    <span>{z.carrier}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EXPRESS & TRUCKING */}
        {activeTab === "trucking" && (
          <div>
            {/* Hero */}
            <div className="flex flex-col md:flex-row gap-10 mb-12">
              <div className="flex-1">
                <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">USA · Canada · Mexico</span>
                <h2 className="text-3xl font-extrabold text-stone-900 mt-1 mb-3">Cross-Border Trucking Services</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-4">
                  Our <strong>60-truck fleet</strong> operates across USA, Canada, and Mexico with full cross-border capability. We handle everything — USMCA compliance, customs clearance, border transfer coordination, and real-time GPS tracking — so your cargo moves seamlessly across North America.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { value: "60", label: "Owned Trucks", icon: "ri-truck-line" },
                    { value: "28", label: "Warehouses", icon: "ri-building-2-line" },
                    { value: "3", label: "Countries", icon: "ri-flag-line" },
                  ].map((s) => (
                    <div key={s.label} className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg mx-auto mb-1.5">
                        <i className={`${s.icon} text-amber-500 text-sm`}></i>
                      </div>
                      <div className="text-xl font-extrabold text-stone-900">{s.value}</div>
                      <div className="text-xs text-stone-500">{s.label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  {[
                    { flag: "🇺🇸", name: "USA", desc: "All 48 states + AK & HI" },
                    { flag: "🇨🇦", name: "Canada", desc: "All provinces & territories" },
                    { flag: "🇲🇽", name: "Mexico", desc: "Major cities & border zones" },
                  ].map((c) => (
                    <div key={c.name} className="flex items-center gap-2 bg-stone-50 border border-stone-100 rounded-xl px-3 py-2">
                      <span className="text-xl">{c.flag}</span>
                      <div>
                        <div className="font-bold text-stone-800 text-xs">{c.name}</div>
                        <div className="text-stone-400 text-xs">{c.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {["USMCA Compliant", "In-House Customs", "Border Transfer", "Real-Time GPS", "24/7 Dispatch", "Dedicated Fleet"].map((tag) => (
                    <span key={tag} className="bg-stone-100 text-stone-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">{tag}</span>
                  ))}
                </div>
                <button onClick={() => setQuoteOpen(true)} className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i> Request Trucking Quote
                </button>
              </div>
              <div className="w-full md:w-[420px] shrink-0 space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=fleet%20of%20semi%20trucks%20highway%20USA%20Canada%20cross%20border%20trucking%20logistics%20professional%20photography%20dramatic%20sky%20interstate%20highway%20modern%20white%20trucks%20lined%20up%20convoy&width=840&height=480&seq=trucking_fleet_new_01&orientation=landscape"
                  alt="60-Truck Fleet"
                  className="w-full h-56 object-cover object-top rounded-2xl"
                />
                <div className="bg-stone-900 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-400 rounded-lg shrink-0">
                      <i className="ri-flag-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-extrabold text-sm">Cross-Border Specialist</h4>
                      <p className="text-stone-400 text-xs">USMCA · CBP · CBSA · SAT Certified</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      "C-TPAT certified carrier for expedited border crossing",
                      "In-house customs brokers at all major border crossings",
                      "Pre-clearance filing before truck reaches the border",
                      "Bonded carrier status for in-transit shipments",
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-2 text-xs">
                        <i className="ri-checkbox-circle-fill text-amber-400 mt-0.5 shrink-0"></i>
                        <span className="text-stone-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Core Services Grid */}
            <h3 className="text-xl font-extrabold text-stone-900 mb-5">Core Trucking Services</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
              {truckingServices.map((s) => (
                <div key={s.title} className="bg-white border border-stone-100 rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                      <i className={`${s.icon} text-amber-500 text-lg`}></i>
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${s.badgeColor}`}>{s.badge}</span>
                  </div>
                  <h4 className="font-extrabold text-stone-900 text-sm mb-1">{s.title}</h4>
                  <p className="text-stone-500 text-xs leading-relaxed mb-3">{s.desc}</p>
                  <div className="space-y-1.5 mb-4">
                    {s.features.map((f) => (
                      <div key={f} className="flex items-center gap-1.5 text-xs text-stone-600">
                        <i className="ri-check-line text-amber-500 shrink-0"></i>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-stone-100">
                    <span className="text-amber-600 font-extrabold text-sm">{s.price}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Cross-border highlight */}
            <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-2xl p-8 mb-12">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-red-500 text-white text-xs font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">VERY IMPORTANT</span>
                    <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">Cross-Border Trucking</span>
                  </div>
                  <h3 className="text-white text-2xl font-extrabold mb-3">Seamless USA ↔ Canada ↔ Mexico</h3>
                  <p className="text-stone-300 text-sm leading-relaxed mb-5">
                    Our cross-border trucking service is the backbone of North American supply chains. With <strong className="text-white">60 trucks</strong>, in-house customs brokers, and C-TPAT certification, we eliminate border delays and keep your cargo moving 24/7.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { icon: "ri-file-text-line", title: "USMCA Compliance", desc: "Full certificate of origin preparation and tariff preference claim filing" },
                      { icon: "ri-government-line", title: "Customs Clearance", desc: "In-house CBP, CBSA & SAT brokers at all major border crossings" },
                      { icon: "ri-exchange-line", title: "Border Transfer", desc: "Seamless driver and trailer exchange at all major border crossing points" },
                    ].map((f) => (
                      <div key={f.title} className="bg-white/10 rounded-xl p-4">
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-400/20 rounded-lg mb-2">
                          <i className={`${f.icon} text-amber-400 text-sm`}></i>
                        </div>
                        <h4 className="text-white font-bold text-xs mb-1">{f.title}</h4>
                        <p className="text-stone-400 text-xs leading-relaxed">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full md:w-64 shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=USA%20Canada%20Mexico%20border%20crossing%20truck%20checkpoint%20customs%20inspection%20professional%20photography%20dramatic%20lighting%20border%20patrol%20international%20trade%20North%20America&width=520&height=420&seq=border_crossing_new_02&orientation=portrait"
                    alt="Cross-Border"
                    className="w-full h-56 object-cover object-top rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Cross-border routes table */}
            <h3 className="text-xl font-extrabold text-stone-900 mb-5">Popular Cross-Border Routes</h3>
            <div className="overflow-x-auto rounded-2xl border border-stone-100 mb-12">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    {["Origin", "Destination", "Transit Time", "Border Crossing", "Frequency"].map((h) => (
                      <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-stone-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {crossBorderRoutes.map((r, i) => (
                    <tr key={i} className="border-t border-stone-100 hover:bg-amber-50/40 transition-colors">
                      <td className="px-5 py-4 font-semibold text-stone-800 whitespace-nowrap">{r.from}</td>
                      <td className="px-5 py-4 text-stone-600 whitespace-nowrap">{r.to}</td>
                      <td className="px-5 py-4"><span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{r.transit}</span></td>
                      <td className="px-5 py-4 text-stone-500 text-xs">{r.border}</td>
                      <td className="px-5 py-4"><span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{r.freq}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Real-time tracking + warehouse integration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-400 rounded-xl shrink-0">
                    <i className="ri-map-pin-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-extrabold text-stone-900 text-base">Real-Time GPS Tracking</h4>
                    <p className="text-stone-500 text-xs">Live updates every 5 minutes</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: "ri-map-2-line", title: "Live Map View", desc: "See your truck's exact location on a live map at any time" },
                    { icon: "ri-notification-3-line", title: "ETA Alerts", desc: "Automated SMS/email alerts for pickup, border crossing, and delivery" },
                    { icon: "ri-history-line", title: "Full Route History", desc: "Complete trip history with timestamps at every checkpoint" },
                    { icon: "ri-smartphone-line", title: "Mobile App Access", desc: "Track all your shipments from our mobile app or web portal" },
                  ].map((f) => (
                    <div key={f.title} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg shrink-0 mt-0.5">
                        <i className={`${f.icon} text-amber-500 text-xs`}></i>
                      </div>
                      <div>
                        <h5 className="font-bold text-stone-800 text-xs">{f.title}</h5>
                        <p className="text-stone-500 text-xs">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-stone-50 border border-stone-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-stone-800 rounded-xl shrink-0">
                    <i className="ri-building-2-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-extrabold text-stone-900 text-base">28 Warehouse Integration</h4>
                    <p className="text-stone-500 text-xs">Trucks connect directly to all warehouses</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { flag: "🇺🇸", region: "USA (5 warehouses)", cities: "LA · NY · Chicago · Houston · Seattle", trucks: "25 trucks assigned" },
                    { flag: "🇨🇦", region: "Canada (3 warehouses)", cities: "Toronto · Vancouver · Montreal", trucks: "20 trucks assigned" },
                    { flag: "🇲🇽", region: "Mexico (cross-border)", cities: "Mexico City · Monterrey · Tijuana", trucks: "15 trucks assigned" },
                  ].map((w) => (
                    <div key={w.region} className="bg-white rounded-xl p-3 border border-stone-100">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{w.flag}</span>
                          <span className="font-bold text-stone-800 text-xs">{w.region}</span>
                        </div>
                        <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">{w.trucks}</span>
                      </div>
                      <p className="text-stone-400 text-xs">{w.cities}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-amber-400 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-white font-extrabold text-xl mb-1">Ready to Move Cargo Across North America?</h3>
                <p className="text-amber-100 text-sm">60 trucks, 28 warehouses, full cross-border capability. Get a quote in under 2 hours.</p>
              </div>
              <button
                onClick={() => setQuoteOpen(true)}
                className="flex items-center gap-2 bg-white hover:bg-stone-50 text-amber-600 font-extrabold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-truck-line"></i> Get Trucking Quote
              </button>
            </div>
          </div>
        )}

        {/* CUSTOMS DECLARATION & CLEARANCE */}
        {activeTab === "customs" && (
          <div>
            {/* Hero intro */}
            <div className="flex flex-col md:flex-row gap-10 mb-14">
              <div className="flex-1">
                <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">China &amp; North America</span>
                <h2 className="text-3xl font-extrabold text-stone-900 mt-1 mb-4">Customs Declaration &amp; Clearance</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-4">
                  Providing <strong>professional customs declaration and clearance services</strong> in China and North America, ensuring smooth and seamless two-way transportation. Our licensed customs brokers handle all documentation, tariff classification, and compliance requirements so your cargo clears borders without delays.
                </p>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  Whether you are exporting from China or importing into the USA and Canada, our end-to-end customs service covers everything from HS code classification to duty payment and ISF filing.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  {["China Export Customs", "USA Import Clearance", "Canada Import Clearance", "ISF Filing", "Duty Drawback", "AMS / ACI Filing"].map((tag) => (
                    <span key={tag} className="bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">{tag}</span>
                  ))}
                </div>
                <button onClick={() => setQuoteOpen(true)} className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i> Request Customs Service
                </button>
              </div>
              <div className="w-full md:w-96 shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=customs%20border%20inspection%20officer%20professional%20port%20terminal%20cargo%20clearance%20official%20documentation%20stamp%20passport%20control%20international%20trade%20compliance%20modern%20office&width=800&height=560&seq=customs_hero_img_01&orientation=landscape"
                  alt="Customs Clearance"
                  className="w-full h-72 object-cover object-top rounded-2xl"
                />
              </div>
            </div>

            {/* Two-way service panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
              {/* China side */}
              <div className="bg-stone-50 border border-stone-100 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 bg-stone-900">
                  <span className="text-2xl">🇨🇳</span>
                  <div>
                    <h3 className="text-white font-extrabold text-base">China Export Customs</h3>
                    <p className="text-stone-400 text-xs">Shenzhen · Shanghai · Guangzhou · Ningbo · Tianjin</p>
                  </div>
                  <span className="ml-auto bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Export</span>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { icon: "ri-file-text-line", title: "Export Declaration Filing", desc: "Accurate HS code classification and customs value declaration for all cargo types" },
                    { icon: "ri-shield-check-line", title: "Commodity Inspection", desc: "CIQ inspection coordination for regulated goods including food, electronics, and chemicals" },
                    { icon: "ri-money-dollar-circle-line", title: "VAT Refund Processing", desc: "Export VAT refund application and follow-up to maximize your tax recovery" },
                    { icon: "ri-file-list-3-line", title: "Export License Support", desc: "Assistance with export licenses for controlled goods and dual-use items" },
                    { icon: "ri-checkbox-circle-line", title: "AEO / CATO Compliance", desc: "Authorized Economic Operator compliance management for expedited clearance" },
                    { icon: "ri-time-line", title: "Same-Day Filing", desc: "Urgent export declarations processed same day for time-critical shipments" },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0 mt-0.5">
                        <i className={`${item.icon} text-amber-500 text-sm`}></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-stone-800 text-sm">{item.title}</h4>
                        <p className="text-stone-500 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* North America side */}
              <div className="bg-stone-50 border border-stone-100 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 bg-stone-900">
                  <span className="text-2xl">🇺🇸🇨🇦</span>
                  <div>
                    <h3 className="text-white font-extrabold text-base">North America Import Clearance</h3>
                    <p className="text-stone-400 text-xs">Los Angeles · New York · Chicago · Toronto · Vancouver</p>
                  </div>
                  <span className="ml-auto bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Import</span>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { icon: "ri-file-text-line", title: "US Customs Entry Filing", desc: "CBP entry filing (Type 01, 03, 11) with accurate HTS classification and valuation" },
                    { icon: "ri-ship-line", title: "ISF 10+2 Filing", desc: "Importer Security Filing submitted 24 hours before vessel departure from origin port" },
                    { icon: "ri-building-line", title: "Canada CBSA Clearance", desc: "CBSA B3 entry filing, RMD, and CERS compliance for all Canadian ports of entry" },
                    { icon: "ri-money-dollar-circle-line", title: "Duty & Tax Management", desc: "Tariff classification review, Section 301 exclusion filing, and duty optimization strategies" },
                    { icon: "ri-search-line", title: "FDA / USDA Compliance", desc: "Prior notice filing, FDA registration, and USDA APHIS coordination for regulated products" },
                    { icon: "ri-refresh-line", title: "Duty Drawback", desc: "Recover up to 99% of duties paid on re-exported goods through our drawback program" },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg shrink-0 mt-0.5">
                        <i className={`${item.icon} text-green-600 text-sm`}></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-stone-800 text-sm">{item.title}</h4>
                        <p className="text-stone-500 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Process timeline */}
            <h3 className="text-xl font-extrabold text-stone-900 mb-6">Two-Way Clearance Process</h3>
            <div className="relative mb-14">
              <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-amber-100 z-0"></div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 relative z-10">
                {[
                  { step: "01", icon: "ri-file-upload-line", title: "Submit Documents", desc: "Commercial invoice, packing list, B/L or AWB, and any required certificates" },
                  { step: "02", icon: "ri-search-eye-line", title: "HS Classification", desc: "Our brokers classify your goods with the correct HS/HTS codes for accurate duty rates" },
                  { step: "03", icon: "ri-computer-line", title: "Electronic Filing", desc: "Customs declaration submitted electronically to China Customs or CBP/CBSA" },
                  { step: "04", icon: "ri-checkbox-circle-line", title: "Customs Release", desc: "Cargo released after inspection or examination — typically within 24–48 hours" },
                  { step: "05", icon: "ri-truck-line", title: "Delivery Arranged", desc: "Cargo handed to trucking or warehouse team for final delivery to destination" },
                ].map((s) => (
                  <div key={s.step} className="flex flex-col items-center text-center">
                    <div className="relative mb-4">
                      <div className="w-16 h-16 flex items-center justify-center bg-white border-2 border-amber-200 rounded-2xl">
                        <i className={`${s.icon} text-amber-500 text-2xl`}></i>
                      </div>
                      <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-amber-400 text-white text-xs font-extrabold rounded-full">{s.step}</span>
                    </div>
                    <h4 className="font-bold text-stone-800 text-sm mb-1">{s.title}</h4>
                    <p className="text-stone-500 text-xs leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Required documents */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6">
                <h4 className="font-extrabold text-stone-900 text-base mb-4 flex items-center gap-2">
                  <i className="ri-file-list-2-line text-amber-500"></i> Required Documents — Export from China
                </h4>
                <div className="space-y-2">
                  {[
                    "Commercial Invoice (in English & Chinese)",
                    "Packing List with net/gross weight and dimensions",
                    "Bill of Lading or Air Waybill",
                    "Export License (if applicable)",
                    "Certificate of Origin (Form A / CO)",
                    "Product Test Reports / Certificates (CE, FCC, etc.)",
                    "Dangerous Goods Declaration (if applicable)",
                  ].map((doc) => (
                    <div key={doc} className="flex items-start gap-2 text-xs">
                      <i className="ri-checkbox-circle-fill text-amber-500 mt-0.5 shrink-0"></i>
                      <span className="text-stone-600">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-2xl p-6">
                <h4 className="font-extrabold text-stone-900 text-base mb-4 flex items-center gap-2">
                  <i className="ri-file-list-2-line text-green-600"></i> Required Documents — Import to USA / Canada
                </h4>
                <div className="space-y-2">
                  {[
                    "Commercial Invoice with declared value",
                    "Packing List",
                    "Bill of Lading (OBL or Telex Release) / AWB",
                    "ISF 10+2 data (USA sea shipments)",
                    "FDA Prior Notice (food, cosmetics, devices)",
                    "USDA Phytosanitary Certificate (wood, plants)",
                    "Importer of Record (IOR) authorization letter",
                  ].map((doc) => (
                    <div key={doc} className="flex items-start gap-2 text-xs">
                      <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 shrink-0"></i>
                      <span className="text-stone-600">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-14">
              {[
                { value: "15,000+", label: "Shipments Cleared Annually", icon: "ri-checkbox-circle-line" },
                { value: "99.2%", label: "First-Pass Clearance Rate", icon: "ri-award-line" },
                { value: "24 hrs", label: "Average Clearance Time", icon: "ri-time-line" },
                { value: "Licensed", label: "CBP & CBSA Brokers", icon: "ri-government-line" },
              ].map((s) => (
                <div key={s.label} className="bg-stone-50 border border-stone-100 rounded-2xl p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl mx-auto mb-3">
                    <i className={`${s.icon} text-amber-500 text-lg`}></i>
                  </div>
                  <div className="text-2xl font-extrabold text-stone-900 mb-1">{s.value}</div>
                  <div className="text-stone-500 text-xs">{s.label}</div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-stone-900 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-white font-extrabold text-xl mb-1">Need Customs Clearance?</h3>
                <p className="text-stone-400 text-sm">Our licensed brokers in China and North America are ready to handle your shipment. Get a quote in 2 hours.</p>
              </div>
              <button
                onClick={() => setQuoteOpen(true)}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-government-line"></i> Get Customs Quote
              </button>
            </div>
          </div>
        )}

        {/* WAREHOUSES */}
        {activeTab === "warehouses" && (
          <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Global Network</span>
                <h2 className="text-3xl font-extrabold text-stone-900 mt-1">28 Overseas Warehouses</h2>
                <p className="text-stone-500 text-sm mt-1">Strategically located across 6 regions for fast last-mile delivery worldwide.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {regions.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRegionFilter(r)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      regionFilter === r ? "bg-amber-400 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {r} {r !== "All" && `(${warehouses.filter((w) => w.region === r).length})`}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredWarehouses.map((w, i) => {
                const colors = regionColors[w.region];
                return (
                  <div key={i} className={`${colors.bg} border rounded-xl p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{w.flag}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-bold ${colors.text}`}>{w.region}</span>
                          <span className="bg-green-100 text-green-600 text-xs font-semibold px-2 py-0.5 rounded-full">Active</span>
                        </div>
                        <p className="text-stone-700 font-semibold text-sm mt-1 truncate">{w.city}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white/70 rounded-lg p-2">
                        <div className={`text-xs font-bold ${colors.text}`}>{w.delivery}</div>
                        <div className="text-xs text-stone-400">Delivery</div>
                      </div>
                      <div className="bg-white/70 rounded-lg p-2">
                        <div className="text-xs font-bold text-stone-700">{w.skus}</div>
                        <div className="text-xs text-stone-400">SKUs</div>
                      </div>
                      <div className="bg-white/70 rounded-lg p-2">
                        <div className="text-xs font-bold text-stone-700">{w.area}</div>
                        <div className="text-xs text-stone-400">Area</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 bg-amber-50 border border-amber-100 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-extrabold text-stone-900 mb-2">Need Warehouse Storage?</h3>
              <p className="text-stone-500 text-sm mb-5">Store your inventory in our nearest warehouse and ship locally for faster delivery and lower costs.</p>
              <button onClick={() => setQuoteOpen(true)} className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-building-2-line"></i> Inquire About Warehouse Space
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Why Choose Us */}
      <section className="bg-stone-50 py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Why CrownGlobal Logistics</span>
            <h2 className="text-3xl font-extrabold text-stone-900 mt-1">Your End-to-End Freight Partner</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: "ri-building-2-line", title: "28 Warehouses", desc: "Strategically placed warehouses across 6 continents for ultra-fast last-mile delivery." },
              { icon: "ri-government-line", title: "Customs Experts", desc: "Licensed CBP & CBSA brokers in China and North America with 99.2% first-pass clearance." },
              { icon: "ri-map-pin-line", title: "Real-Time Tracking", desc: "Track every shipment live — sea, air, express, or truck — from origin to destination." },
              { icon: "ri-money-dollar-circle-line", title: "Competitive Rates", desc: "Volume discounts, consolidated shipping, and transparent pricing with no hidden fees." },
              { icon: "ri-customer-service-2-line", title: "24/7 Support", desc: "Dedicated logistics experts available around the clock in English, Chinese, and Spanish." },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 border border-stone-100 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-2xl mx-auto mb-4">
                  <i className={`${f.icon} text-amber-500 text-2xl`}></i>
                </div>
                <h4 className="font-extrabold text-stone-800 text-base mb-2">{f.title}</h4>
                <p className="text-stone-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-stone-900 mb-3">Ready to Ship Globally?</h2>
          <p className="text-stone-500 text-sm mb-8 max-w-xl mx-auto">Get a free quote for sea freight, air freight, express delivery, or trucking services. Our team responds within 2 hours.</p>
          <button
            onClick={() => setQuoteOpen(true)}
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-extrabold px-10 py-4 rounded-full text-base transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line"></i> Get a Free Quote Now
          </button>
        </div>
      </section>

      <Footer />

      {/* Quote Modal */}
      {quoteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
              <h3 className="font-extrabold text-stone-900 text-lg">Request a Free Quote</h3>
              <button onClick={() => setQuoteOpen(false)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {quoteSubmitted ? (
              <div className="px-6 py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                </div>
                <h4 className="font-extrabold text-stone-900 text-lg mb-2">Quote Request Sent!</h4>
                <p className="text-stone-500 text-sm">Our logistics team will contact you within 2 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleQuoteSubmit} className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-stone-600 mb-1 block">Full Name</label>
                    <input
                      type="text"
                      required
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-stone-600 mb-1 block">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-600 mb-1 block">Service Type</label>
                  <select
                    value={quoteForm.service}
                    onChange={(e) => setQuoteForm({ ...quoteForm, service: e.target.value })}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400 bg-white cursor-pointer"
                  >
                    <option>Sea Freight (FCL)</option>
                    <option>Sea Freight (LCL)</option>
                    <option>Air Freight</option>
                    <option>Express Delivery</option>
                    <option>Trucking (FTL)</option>
                    <option>Trucking (LTL)</option>
                    <option>Cross-Border Trucking (USA ↔ Canada)</option>
                    <option>Cross-Border Trucking (USA ↔ Mexico)</option>
                    <option>Dedicated Fleet Contract</option>
                    <option>Customs Clearance (China Export)</option>
                    <option>Customs Clearance (USA Import)</option>
                    <option>Customs Clearance (Canada Import)</option>
                    <option>Warehouse Storage</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-stone-600 mb-1 block">Origin</label>
                    <input
                      type="text"
                      value={quoteForm.origin}
                      onChange={(e) => setQuoteForm({ ...quoteForm, origin: e.target.value })}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400"
                      placeholder="e.g. Shanghai, China"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-stone-600 mb-1 block">Destination</label>
                    <input
                      type="text"
                      value={quoteForm.destination}
                      onChange={(e) => setQuoteForm({ ...quoteForm, destination: e.target.value })}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400"
                      placeholder="e.g. Los Angeles, USA"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-600 mb-1 block">Cargo Weight / Volume</label>
                  <input
                    type="text"
                    value={quoteForm.weight}
                    onChange={(e) => setQuoteForm({ ...quoteForm, weight: e.target.value })}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400"
                    placeholder="e.g. 500 kg / 2 CBM"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-600 mb-1 block">Additional Notes</label>
                  <textarea
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                    maxLength={500}
                    rows={3}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400 resize-none"
                    placeholder="Special requirements, cargo type, etc."
                  />
                </div>
                <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  Submit Quote Request
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
